import { useTranslation } from 'react-i18next';

import type { InvoiceTemplate } from '~/api/invoiceTemplates/types';

import { AlertDialog, Button, Menu } from '~/components';
import { optionsMenuTriggerStyle } from '~/components/Table/TableRow.css';

import { ConfirmDeleteInvoiceTemplateDialog } from '../../components/ConfirmDeleteInvoiceTemplateDialog';
import {
  useOnCreateInvoiceFromTemplate,
  useOnDeleteInvoiceTemplate,
  useOnEditInvoiceTemplate,
  useOnPauseInvoiceTemplate,
  useOnShowInvoiceTemplate,
  useOnUnpauseInvoiceTemplate,
} from '../../invoiceTemplates.hooks';

type Props = {
  invoiceTemplate: InvoiceTemplate;
};

export const InvoiceTemplateOptionsCell = ({ invoiceTemplate }: Props) => {
  const { onShow } = useOnShowInvoiceTemplate(invoiceTemplate);
  const { onEdit } = useOnEditInvoiceTemplate(invoiceTemplate);
  const { isPending: isCreateInvoicePending, onCreateInvoiceFromTemplate } = useOnCreateInvoiceFromTemplate(invoiceTemplate);
  const { isPending: isPausePending, onPause } = useOnPauseInvoiceTemplate(invoiceTemplate);
  const { isPending: isUnpausePending, onUnpause } = useOnUnpauseInvoiceTemplate(invoiceTemplate);
  const { confirmDelete, deleteHandle, isPending: isDeletePending, onDelete } = useOnDeleteInvoiceTemplate(invoiceTemplate);

  const { t } = useTranslation(['common', 'invoiceTemplates']);

  const canPause = invoiceTemplate.intervalStatus === 'Live';
  const canUnpause = invoiceTemplate.intervalStatus === 'Paused';

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Menu.Root>
        <Menu.Trigger render={<Button aria-label={t('common:actions.openMenu')} className={optionsMenuTriggerStyle} icon="MoreVert" />} />

        <Menu.List>
          <Menu.Item icon="File" onClick={onShow}>
            {t('invoiceTemplates:actions.details')}
          </Menu.Item>

          <Menu.Item icon="Edit" onClick={onEdit}>
            {t('invoiceTemplates:actions.edit')}
          </Menu.Item>

          <Menu.Item disabled={isCreateInvoicePending} icon="RequestQuote" onClick={onCreateInvoiceFromTemplate}>
            {t('invoiceTemplates:actions.createInvoice')}
          </Menu.Item>

          {canPause && (
            <Menu.Item disabled={isPausePending} icon="Pause" onClick={onPause}>
              {t('invoiceTemplates:actions.pause')}
            </Menu.Item>
          )}

          {canUnpause && (
            <Menu.Item disabled={isUnpausePending} icon="Play" onClick={onUnpause}>
              {t('invoiceTemplates:actions.unpause')}
            </Menu.Item>
          )}

          <Menu.Item icon="Trash" onClick={onDelete}>
            {t('invoiceTemplates:actions.delete')}
          </Menu.Item>
        </Menu.List>
      </Menu.Root>

      <AlertDialog.Root handle={deleteHandle}>
        <ConfirmDeleteInvoiceTemplateDialog loading={isDeletePending} onConfirm={confirmDelete} />
      </AlertDialog.Root>
    </div>
  );
};
